// Liste explicative d'un indicateur des recettes : colonnes servies par GET /indicateurs/liste
// (mêmes filtres et même requête de base que la valeur, R16), montants en francs, lien vers la
// fiche de chaque objet, somme ou nombre des lignes rappelé en tête, export CSV de la liste.
import { Bouton, EtatVide, Lien, LienBouton, Squelette, Tableau, type ColonneTableau } from '@cc/ui';
import { ArrowLeft, Download } from 'lucide-react';
import { useParams, useSearchParams } from 'react-router';
import { EnTetePage } from '../../commun/EnTetePage';
import { AffichageErreur } from '../../commun/EtatRequete';
import { fraicheur } from '../../commun/format';
import { telechargerCsv } from '../metier/csv';
import { calculerPeriode } from '../tableau/periode';
import { LIBELLES_ETAT_ANOMALIE, LIBELLES_ETAT_CREANCE, LIBELLES_ETAT_RECU, LIBELLES_TYPE_ANOMALIE, francs } from './donnees';
import { NavigationFinances } from './NavigationFinances';
import { useListeRec } from './requetes';
import { definitionRec, valeurLisible } from './TuileRecette';
import styles from './recettes.module.css';

type Ligne = Record<string, unknown>;
const COLONNES_MONTANT = new Set(['montant', 'du', 'paye']);
const LIBELLES_ETATS: Record<string, string> = { ...LIBELLES_ETAT_CREANCE, ...LIBELLES_ETAT_RECU, ...LIBELLES_ETAT_ANOMALIE };

/** Les montants des listes arrivent en nombres ou en chaînes numériques (colonnes numeric de PostgreSQL). */
export function nombre(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string' && /^-?\d+(\.\d+)?$/.test(v.trim())) return Number(v);
  return null;
}

function hrefObjet(l: Ligne): string | null {
  const id = typeof l.id === 'string' ? l.id : null;
  if (!id) return null;
  const chemins: Record<string, string> = { creance: '/recettes/creances', paiement: '/recettes/paiements', anomalie: '/recettes/anomalies', remboursement: '/recettes/remboursements' };
  const base = chemins[String(l.objet_type)];
  return base ? `${base}/${id}` : null;
}

export function texteCellule(cle: string, valeur: unknown): string {
  if (valeur === null || valeur === undefined || valeur === '') return 'non renseigné';
  if (COLONNES_MONTANT.has(cle) && nombre(valeur) !== null) return francs(nombre(valeur));
  if (cle === 'etat') return LIBELLES_ETATS[String(valeur)] ?? String(valeur);
  if (cle === 'type') return LIBELLES_TYPE_ANOMALIE[String(valeur)] ?? String(valeur);
  if (cle === 'echue') return valeur === 'oui' ? 'Oui' : 'Non';
  return String(valeur);
}

/** Somme de la colonne montant, ou nombre de lignes : la valeur attendue de la tuile (R16). */
export function totalLignes(lignes: Ligne[], unite: 'montant' | 'taux' | 'nombre'): number | null {
  if (unite === 'nombre') return lignes.length;
  if (unite === 'montant') return lignes.reduce((s, l) => s + (nombre(l.montant) ?? 0), 0);
  const du = lignes.reduce((s, l) => s + (nombre(l.du) ?? 0), 0);
  const paye = lignes.reduce((s, l) => s + (nombre(l.paye) ?? 0), 0);
  return du > 0 ? Math.round((1000 * paye) / du) / 10 : null;
}

export function PageListeRec() {
  const { code = '' } = useParams();
  const [params] = useSearchParams();
  const definition = definitionRec(code);
  const defaut = calculerPeriode('mois');
  const periode = { debut: params.get('debut') ?? defaut.debut, fin: params.get('fin') ?? defaut.fin };
  const liste = useListeRec(code, periode, Boolean(definition));

  if (!definition) {
    return (
      <EtatVide titre="Indicateur inconnu" niveau={2} action={<LienBouton href="/recettes">Revenir aux finances</LienBouton>}>
        <p>Le code {code} ne figure pas parmi les indicateurs des recettes.</p>
      </EtatVide>
    );
  }
  const colonnes: ColonneTableau<Ligne>[] = (liste.data?.colonnes ?? []).map((c, i) => ({
    id: c.cle,
    entete: c.libelle,
    enTeteLigne: i === 0,
    masquable: i !== 0,
    numerique: COLONNES_MONTANT.has(c.cle),
    valeurTri: (l) => nombre(l[c.cle]) ?? (l[c.cle] === null || l[c.cle] === undefined ? null : String(l[c.cle])),
    cellule: (l) => {
      const texte = texteCellule(c.cle, l[c.cle]);
      const href = i === 0 ? hrefObjet(l) : null;
      if (href) return <Lien href={href}>{texte}</Lien>;
      return COLONNES_MONTANT.has(c.cle) ? <span className={styles.montant}>{texte}</span> : texte;
    },
  }));
  const lignes = liste.data?.elements ?? [];
  const total = liste.data ? totalLignes(lignes, definition.unite) : null;

  return (
    <>
      <EnTetePage
        etiquette={`Indicateur ${definition.code}`}
        titre={definition.libelle}
        description={
          <>
            {definition.description}. {definition.instantane ? 'Situation à date.' : `Période du ${periode.debut} au ${periode.fin}.`}
            {liste.data?.calcule_le ? ` Calculé ${fraicheur(liste.data.calcule_le)}.` : ''}
          </>
        }
        actions={
          <>
            <LienBouton href="/recettes" icone={ArrowLeft}>
              Vue d&apos;ensemble
            </LienBouton>
            {lignes.length > 0 ? (
              <Bouton
                variante="secondaire"
                icone={Download}
                onClick={() =>
                  telechargerCsv(
                    `${definition.code}-${periode.debut}-${periode.fin}.csv`,
                    (liste.data?.colonnes ?? []).map((c) => c.libelle),
                    lignes.map((l) => (liste.data?.colonnes ?? []).map((c) => texteCellule(c.cle, l[c.cle]))),
                  )
                }
              >
                Exporter en CSV
              </Bouton>
            ) : null}
          </>
        }
      />
      <NavigationFinances />
      {liste.isPending ? (
        <Squelette lignes={6} />
      ) : liste.error ? (
        <AffichageErreur erreur={liste.error} lot="L2-A" fonction="La liste explicative de cet indicateur" />
      ) : liste.data.couverture.etat === 'non_active' ? (
        <EtatVide titre="Module non activé">
          <p>Le module des recettes (M07) est désactivé : l&apos;indicateur n&apos;est pas calculé.</p>
        </EtatVide>
      ) : lignes.length === 0 ? (
        <EtatVide titre="Aucune ligne">
          <p>Aucun élément ne correspond à cet indicateur pour cette période.</p>
        </EtatVide>
      ) : (
        <>
          <p role="status" data-testid="nombre-lignes">
            {lignes.length.toLocaleString('fr-FR')} ligne{lignes.length > 1 ? 's' : ''}
            {total !== null && definition.unite !== 'nombre' ? `, ${definition.unite === 'taux' ? 'taux' : 'total'} ${valeurLisible(total, definition.unite)}` : ''}
          </p>
          <Tableau legende={`Liste explicative ${definition.code}`} colonnes={colonnes} lignes={lignes} cleLigne={(l) => `${String(l.objet_type)}-${String(l.id)}`} />
        </>
      )}
    </>
  );
}
